"use client"

import * as React from "react"
import Image from "next/image"
import NextLink from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useOrganizationList } from "@clerk/nextjs"
import { useQueryClient } from "@tanstack/react-query"
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuItem,
  Skeleton,
  useSidebar,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { Briefcase, Building2, Check, ChevronDown, Cog, Library, LogOut, Puzzle } from "lucide-react"
import { useTheme } from "next-themes"

import { isAllBooksId } from "@/lib/all-books"
import { useFetchBackOffices } from "@/hooks/api/back-offices"
import { IdentityCompany, useFetchIdentityCompanies } from "@/hooks/api/identity-companies"
import { useOrganizations } from "@/hooks/api/organizations/queries"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { Link } from "@/components/book-link"
import { BookIcon } from "@/components/books/book-icon"
import { BookSwitcherMenuItems } from "@/components/layout/book-switcher"

export function SidebarSettings() {
  const router = useRouter()
  const pathname = usePathname()
  const queryClient = useQueryClient()
  const { state: sidebarState } = useSidebar()
  const { isLoaded: organizationsLoaded, setActive } = useOrganizationList()
  const { data: currentUser, isLoading } = useFetchCurrentUser()
  const { data: identityCompanies } = useFetchIdentityCompanies()
  const { data: backOfficesResponse } = useFetchBackOffices()
  const { resolvedTheme } = useTheme()
  const bookId = useBookId()
  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false)
  const [companySearch, setCompanySearch] = React.useState("")
  const [companySwitcherOpen, setCompanySwitcherOpen] = React.useState(false)
  const [bookSwitcherOpen, setBookSwitcherOpen] = React.useState(false)

  const company = currentUser?.company
  const showCompanySwitcher = (identityCompanies?.companies.length ?? 0) > 1
  const useCompanyDropdown = (identityCompanies?.companies.length ?? 0) > 6
  const companies = [...(identityCompanies?.companies ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    .filter((company) => company.name.toLowerCase().includes(companySearch.trim().toLowerCase()))
  const { data: orgAccess } = useOrganizations()
  const hasOrgAccess = (orgAccess?.organizations.length ?? 0) > 0
  const hasBackOfficeAccess = (backOfficesResponse?.back_offices.length ?? 0) > 0
  const currentBookId = bookId ?? pathname.split("/")[1]
  const hasMultipleBooks = (currentUser?.books.length ?? 0) > 1
  const currentBook =
    currentUser?.books.find((book) => book.id === currentBookId) ??
    currentUser?.default_book ??
    currentUser?.principal_books?.[0]
  const logoUrl = React.useMemo(() => {
    if (!company) return null
    const { logo_url, dark_mode_logo_url } = company
    if (resolvedTheme === "dark") return dark_mode_logo_url || logo_url
    return logo_url || dark_mode_logo_url
  }, [company, resolvedTheme])

  const switchCompany = async (targetCompany: IdentityCompany) => {
    if (!organizationsLoaded || targetCompany.id === company?.id) return

    await setActive?.({ organization: targetCompany.clerk_organization_id })
    queryClient.clear()
    localStorage.removeItem("slant-query-cache")
    window.location.assign("/redirect")
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu
          open={accountMenuOpen}
          onOpenChange={(open) => {
            setAccountMenuOpen(open)
            if (!open) {
              setCompanySearch("")
              setCompanySwitcherOpen(false)
              setBookSwitcherOpen(false)
            }
          }}
        >
          <div className="flex items-center group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[collapsible=icon]:justify-center">
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "hover:bg-sidebar-accent flex h-9 w-full min-w-0 flex-none justify-between overflow-hidden px-2 py-0 group-data-[collapsible=icon]:justify-center! group-data-[collapsible=icon]:p-0!",
                  hasMultipleBooks
                    ? "group-data-[collapsible=icon]:hover:bg-sidebar-accent-hover group-data-[collapsible=icon]:bg-tertiary group-data-[collapsible=icon]:m-[3px] group-data-[collapsible=icon]:size-[30px]! group-data-[collapsible=icon]:rounded-lg"
                    : "group-data-[collapsible=icon]:size-9!"
                )}
                aria-label="Account and settings"
                title="Account and settings"
              >
                <span className="flex h-6 min-w-0 flex-1 items-center gap-2 overflow-hidden text-left text-sm font-semibold whitespace-nowrap group-data-[collapsible=icon]:hidden">
                  {hasMultipleBooks &&
                    (isAllBooksId(currentBookId) ? (
                      <span className="bg-tertiary flex size-6 shrink-0 items-center justify-center rounded-[3px] min-[1440px]:hidden">
                        <Library className="text-sidebar-foreground size-4" />
                      </span>
                    ) : (
                      <BookIcon
                        book={currentBook}
                        className="bg-tertiary size-6 rounded-[3px] min-[1440px]:hidden"
                        textClassName="text-[10px] font-medium"
                      />
                    ))}
                  {isLoading ? (
                    <Skeleton className="h-4 w-24 shrink-0" />
                  ) : logoUrl ? (
                    <Image
                      src={logoUrl}
                      alt={company?.name ?? "Company logo"}
                      width={168}
                      height={24}
                      className="h-6 w-auto max-w-none shrink-0 object-contain object-left"
                      unoptimized
                    />
                  ) : (
                    <span className="truncate">{company?.name}</span>
                  )}
                </span>
                <span className="hidden items-center justify-center group-data-[collapsible=icon]:flex">
                  {hasMultipleBooks ? (
                    isAllBooksId(currentBookId) ? (
                      <span className="bg-tertiary flex size-6 items-center justify-center rounded-[3px] group-data-[collapsible=icon]:size-[30px]! group-data-[collapsible=icon]:rounded-lg group-data-[collapsible=icon]:bg-transparent">
                        <Library className="text-sidebar-foreground size-4" />
                      </span>
                    ) : (
                      <BookIcon
                        book={currentBook}
                        className="bg-tertiary size-6 rounded-[3px] group-data-[collapsible=icon]:size-[30px]! group-data-[collapsible=icon]:rounded-lg group-data-[collapsible=icon]:bg-transparent"
                        textClassName="text-[10px] font-medium"
                      />
                    )
                  ) : (
                    <>
                      <Image
                        src="/sand_icon_900.png"
                        alt="Slant"
                        width={20}
                        height={20}
                        className="size-5 object-contain dark:hidden"
                      />
                      <Image
                        src="/sand_icon_300.png"
                        alt=""
                        width={20}
                        height={20}
                        className="hidden size-5 object-contain dark:block"
                      />
                    </>
                  )}
                </span>
                <ChevronDown className="text-icon size-4 shrink-0 group-data-[collapsible=icon]:hidden" />
              </Button>
            </DropdownMenuTrigger>
          </div>
          <DropdownMenuContent
            className="max-h-(--radix-dropdown-menu-content-available-height) w-(--radix-dropdown-menu-trigger-width) min-w-56 overflow-y-auto rounded-lg"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
              {currentUser?.email}
            </DropdownMenuLabel>
            {(showCompanySwitcher || hasBackOfficeAccess || hasOrgAccess) && (
              <>
                {showCompanySwitcher && (
                  <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">Company</DropdownMenuLabel>
                )}
                {showCompanySwitcher &&
                  !useCompanyDropdown &&
                  companies.map((targetCompany) => (
                    <DropdownMenuItem
                      key={targetCompany.id}
                      disabled={!organizationsLoaded}
                      onSelect={() => {
                        setAccountMenuOpen(false)
                        void switchCompany(targetCompany)
                      }}
                    >
                      <Building2 className="text-icon" />
                      <span className="min-w-0 flex-1 truncate">{targetCompany.name}</span>
                      {targetCompany.id === company?.id && <Check className="text-icon" />}
                    </DropdownMenuItem>
                  ))}
                {showCompanySwitcher && useCompanyDropdown && (
                  <DropdownMenuItem
                    onSelect={(event) => {
                      event.preventDefault()
                      setCompanySearch("")
                      setCompanySwitcherOpen(!companySwitcherOpen)
                      setBookSwitcherOpen(false)
                    }}
                    aria-expanded={companySwitcherOpen}
                  >
                    <Building2 className="text-icon" />
                    <span className="min-w-0 flex-1 truncate">{company?.name}</span>
                    <ChevronDown className={cn("text-icon", companySwitcherOpen && "rotate-180")} />
                  </DropdownMenuItem>
                )}
                {showCompanySwitcher && useCompanyDropdown && companySwitcherOpen && (
                  <Command
                    shouldFilter={false}
                    onKeyDown={(event) => {
                      if (event.key === "Tab" && !event.shiftKey) {
                        const nextItem = event.currentTarget
                          .closest('[role="menu"]')
                          ?.querySelector<HTMLElement>('[role="menuitem"]:not([data-disabled])')
                        if (nextItem) {
                          event.preventDefault()
                          nextItem.focus()
                        }
                      }
                      if (event.key !== "Escape") event.stopPropagation()
                    }}
                  >
                    <CommandInput
                      aria-label="Search companies"
                      placeholder="Search companies..."
                      className="h-9"
                      wrapperStyles="px-2"
                      value={companySearch}
                      onValueChange={setCompanySearch}
                      autoFocus
                    />
                    <CommandList className="max-h-64 border-t">
                      <CommandEmpty>No companies found</CommandEmpty>
                      <CommandGroup>
                        {companies.map((targetCompany) => (
                          <CommandItem
                            key={targetCompany.id}
                            value={targetCompany.id}
                            disabled={!organizationsLoaded}
                            onSelect={() => {
                              setAccountMenuOpen(false)
                              setCompanySearch("")
                              setCompanySwitcherOpen(false)
                              void switchCompany(targetCompany)
                            }}
                          >
                            <Building2 className="text-icon" />
                            <span className="min-w-0 flex-1 truncate">{targetCompany.name}</span>
                            {targetCompany.id === company?.id && <Check className="text-icon" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                )}
                {orgAccess?.organizations.map((org) => (
                  <DropdownMenuItem key={org.id} asChild>
                    <NextLink href={`/organizations/${org.id}/home`}>
                      <Building2 className="text-icon" />
                      {org.name} · Organization
                    </NextLink>
                  </DropdownMenuItem>
                ))}
                {hasBackOfficeAccess && (
                  <DropdownMenuItem asChild className="gap-2 p-2">
                    <NextLink href="/back-offices">
                      <Briefcase className="text-icon" />
                      Back office
                    </NextLink>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuGroup className={cn(sidebarState === "expanded" && "min-[1440px]:hidden")}>
              <BookSwitcherMenuItems
                open={bookSwitcherOpen}
                onOpenChange={(open) => {
                  setBookSwitcherOpen(open)
                  setCompanySwitcherOpen(false)
                  setCompanySearch("")
                }}
                onSelect={() => {
                  setAccountMenuOpen(false)
                  setCompanySearch("")
                  setCompanySwitcherOpen(false)
                  setBookSwitcherOpen(false)
                }}
              />
            </DropdownMenuGroup>
            <DropdownMenuItem asChild className="gap-2 p-2">
              <NextLink href="/settings/profile">
                <Cog className="text-icon" />
                Settings
              </NextLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="gap-2 p-2">
              <Link href="/integration-requests">
                <Puzzle className="text-icon" />
                Integration requests
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => router.push("/logout")}>
              <LogOut className="text-icon" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
